import { useRef, useState } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Flex,
  useColorModeValue,
} from '@chakra-ui/react';

// TODO: reemplazar el `logo` (placeholder) por la imagen real cuando se tengan.
import saInversionesLogo from '../assets/images/SA Inversiones y Logística S.A.S.webp';
import seincocLogo from '../assets/images/SEINCOC.webp';
import idkLogo from '../assets/images/IDKSS.webp';

const allies = [
  {
    name: 'SA Inversiones y Logística S.A.S.',
    logo: saInversionesLogo,
    description: 'Empresa líder en soluciones de abastecimiento y soporte empresarial. Su experiencia integra la gestión logística, el suministro continuo de alimentos y el diseño de sistemas de refrigeración especializados (incluyendo climatización y cuartos fríos para productos médicos), asegurando que la operación de sus clientes funcione siempre sin interrupciones.',
  },
  {
    name: 'SEINCOC Ingeniería S.A.S.',
    logo: seincocLogo,
    description: 'Aliado estratégico en el sector de la construcción y las obras civiles. Con más de una década de experiencia, desarrollan proyectos integrales de infraestructura y edificaciones, aportando eficiencia, seguridad y responsabilidad técnica a cada obra.',
  },
  {
    name: 'IDK Software Solutions',
    logo: idkLogo,
    description: 'Aliado estratégico en transformación digital y modernización operativa. Especialistas en desarrollo de software a medida, automatización de flujos de trabajo, plataformas web e integración de sistemas cloud y IA. Diseñamos herramientas tecnológicas optimizadas para agilizar la gestión operativa, reducir tiempos de respuesta y asegurar la escalabilidad digital de empresas en crecimiento.'
  }
];

function AllyCard({ ally }) {
  const cardBg = useColorModeValue('rgba(255, 255, 255, 0.8)', 'rgba(15, 23, 42, 0.6)');
  const cardBorder = useColorModeValue('gray.200', 'whiteAlpha.200');
  const cardTextColor = useColorModeValue('gray.600', 'gray.400');
  const titleColor = useColorModeValue('gray.800', 'white');
  const logoBg = useColorModeValue('gray.100', 'rgba(0, 0, 0, 0.8)');
  const logoTextColor = useColorModeValue('gray.400', 'gray.500');

  return (
    <VStack
      bg={cardBg}
      borderRadius="2xl"
      border="1px solid"
      borderColor={cardBorder}
      p={8}
      spacing={5}
      align="center"
      textAlign="center"
      h="full"
    >
      {/* Logo */}
      <Flex
        w="160px"
        h="160px"
        bg={logoBg}
        borderRadius="xl"
        align="center"
        justify="center"
        overflow="hidden"
        flexShrink={0}
      >
        {ally.logo ? (
          <Box
            as="img"
            src={ally.logo}
            alt={ally.name}
            objectFit="contain"
            w="full"
            h="full"
            loading="lazy"
            bgColor="black"
          />
        ) : (
          <Text fontSize="xs" color={logoTextColor} fontWeight="medium">
            Logo
          </Text>
        )}
      </Flex>

      <Heading as="h3" fontSize="xl" color={titleColor} fontWeight="700">
        {ally.name}
      </Heading>

      <Text fontSize="sm" color={cardTextColor} lineHeight="tall" textAlign="justify">
        {ally.description}
      </Text>
    </VStack>
  );
}

function AlliesSection() {
  const subtitleColor = useColorModeValue('gray.600', 'gray.400');
  const scrollRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);

  // Actualiza el dot activo según la tarjeta más centrada en el scroll horizontal.
  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const index = Math.round(el.scrollLeft / el.clientWidth);
    setActiveIndex(index);
  };

  const scrollToIndex = (index) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ left: index * el.clientWidth, behavior: 'smooth' });
  };

  return (
    <Box id="aliados" as="section" w="full">
      <Container maxW="1120px">
        <VStack spacing={10} align="stretch">
          {/* Header */}
          <VStack align="start" spacing={3} maxW="1000px">
            <Heading
              as="h2"
              fontSize={{ base: '3xl', md: '4xl', lg: '5xl' }}
              fontWeight={900}
            >
              Empresas Aliadas
            </Heading>
            <Text
              fontSize={{ base: 'lg', md: 'xl' }}
              color={subtitleColor}
              letterSpacing="wide"
              lineHeight="tall"
              pb={3}
              mb={2}
            >
              Trabajamos de la mano con aliados estratégicos que fortalecen cada etapa de tu proceso.
            </Text>
          </VStack>

          {/* Desktop: tarjetas centradas */}
          <Flex
            justify="center"
            gap={6}
            display={{ base: 'none', lg: 'flex' }}
          >
            {allies.map((ally) => (
              <Box key={ally.name} flex="1" maxW="380px">
                <AllyCard ally={ally} />
              </Box>
            ))}
          </Flex>

          {/* Móvil/tablet: galería deslizable */}
          <Box display={{ base: 'block', lg: 'none' }}>
            <Flex
              ref={scrollRef}
              onScroll={handleScroll}
              overflowX="auto"
              scrollSnapType="x mandatory"
              gap={4}
              pb={2}
              css={{
                scrollbarWidth: 'none',
                '&::-webkit-scrollbar': { display: 'none' },
              }}
            >
              {allies.map((ally) => (
                <Box
                  key={ally.name}
                  flex="0 0 100%"
                  scrollSnapAlign="center"
                >
                  <AllyCard ally={ally} />
                </Box>
              ))}
            </Flex>

            {/* Dots */}
            <HStack justify="center" spacing={2} mt={5}>
              {allies.map((ally, index) => (
                <Box
                  key={ally.name}
                  as="button"
                  aria-label={`Ir a ${ally.name}`}
                  w={activeIndex === index ? '24px' : '8px'}
                  h="8px"
                  borderRadius="full"
                  bg={activeIndex === index ? 'brand.500' : 'whiteAlpha.400'}
                  transition="all 0.3s"
                  onClick={() => scrollToIndex(index)}
                />
              ))}
            </HStack>
          </Box>
        </VStack>
      </Container>
    </Box>
  );
}

export default AlliesSection;
